<template>
    <div>
        <breadcrumbs class="mb-3" />
        <div class="d-flex mb-4 align-center flex-wrap">
            <v-btn variant="flat" :to="{name: 'company-client-profile', params: {id: 0}}" class="bg-primary me-2 mb-2">
                {{ $t('new_corporate_client_profile') }}
            </v-btn>
            <v-btn
                class="me-2 mb-2"
                variant="outlined"
                color="primary"
                :disabled="!selectedCompanies.length"
                data-cy="show-edit-companies-modal"
                @click="isEditCompaniesModal = true"
            >
                {{ $t('edit_clients_profiles') }}
            </v-btn>
            <v-btn
                v-if="isTO1"
                variant="outlined"
                color="primary"
                :disabled="!emails.length"
                class="me-2 mb-2"
                @click="emailModal = true"
            >
                {{ $t('send_email') }}
            </v-btn>
            <file-download-modal
                v-if="isTO1"
                v-model="isShowDownloadExcelMenu"
                :radio-items="downloadRadioItems"
                :label="$t('corporate_clients_download')"
                :loading="isLoadingDownloadExcel"
                @change-radio-group="radioGroupDownloadExcel = $event"
                @download="downloadFile"
            />
            <file-upload-modal
                v-if="isTO1"
                v-model="isShowUploadExcelMenu"
                class="ms-4"
                :label="$t('corporate_clients_upload')"
                :sample-link="sampleLink"
                @upload="uploadExcel"
            />
        </div>
        <companies-table
            ref="table"
            :company-type="['CORPORATOR']"
            @change-selected-emails="emails = $event"
            @change-selected-companies="selectedCompanies = $event"
        />
        <EditCompaniesModal v-model="isEditCompaniesModal" :companies="selectedCompanies" @sent="updateData" />
        <send-clients-email-modal v-model="emailModal" :emails="emails" @sent="$refs.table.selected = []" />
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import CompaniesTable from '@/components/account/CompaniesTable.vue'

    import SendClientsEmailModal from '@/components/account/modals/SendClientsEmailModal.vue'
    import FileUploadModal from '~/components/account/snippets/FileUploadModal.vue'
    import {fullUrl} from '~/utils/host-adapter'
    import FileDownloadModal from '~/components/account/snippets/FileDownloadModal.vue'
    import {downloadData} from '~/utils/helpers'
    import EditCompaniesModal from '~/components/account/companies/EditCompaniesModal.vue'

    @Component({
        components: {
            EditCompaniesModal,
            FileDownloadModal,
            FileUploadModal,
            SendClientsEmailModal,
            Breadcrumbs,
            CompaniesTable,
        },
    })
    export class CompanyCorporateClientsPage extends Vue {
        emails = []
        emailModal = false

        selectedCompanies: number[] = []
        isEditCompaniesModal: boolean = false

        isShowDownloadExcelMenu = false
        isLoadingDownloadExcel = false
        radioGroupDownloadExcel = ''

        isShowUploadExcelMenu = false
        isLoadingUploadExcel = false

        updateData(): void {
            this.$refs.table.selected = []
            this.$refs.table.loadCompanies()
        }

        async uploadExcel(file) {
            this.isLoadingUploadExcel = true
            const formData = new FormData()
            formData.append('file', file)
            try {
                this.result = await this.$api.importOrganisations.post(formData)
                this.isShowUploadExcelMenu = false
                this.updateData()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.isLoadingUploadExcel = false
            }
        }

        async downloadFile(format) {
            this.isLoadingDownloadExcel = true

            try {
                let data

                if (this.radioGroupDownloadExcel === 'selected') {
                    data = await this.$api.downloadCompanies.get({
                        companyIds: this.selectedCompanies,
                        companyType: 'CORPORATE_CLIENT',
                        fileType: format.fileType,
                    })
                } else {
                    data = await this.$api.downloadCompanies.get({
                        filterKey: runtimeStore.companiesFilterKey,
                        companyType: 'CORPORATE_CLIENT',
                        fileType: format.fileType,
                    })
                }

                await downloadData(data, 'corporate_clients.xlsx')
                this.isShowDownloadExcelMenu = false
            } catch (e) {
                this.$toast.error(this.$t('error_message.load_error'))
            } finally {
                this.isLoadingDownloadExcel = false
            }
        }

        get isTO1() {
            return authStore.isTO1
        }

        get sampleLink() {
            return fullUrl('/stat/files/CompaniesUsersImport.xlsx')
        }

        get downloadRadioItems() {
            return [
                {
                    value: 'selected',
                    label: `${this.$t('selected_corporate_clients')} ${this.selectedCompanies?.length || 0}`,
                    disabled: !this.selectedCompanies?.length,
                },
                {value: 'all', label: this.$t('all_corporate_clients')},
            ]
        }
    }

    export default CompanyCorporateClientsPage
</script>
